import React from "react";
import { Badge, Box, Flex } from "@chakra-ui/react";
import { FiMenu } from "react-icons/fi";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { ColumnConfig, QuoteColumnType } from "./column-types";

export interface ColumnHeaderCellProps {
	column: ColumnConfig;
	columnCount: number;
	columnIndex: number;
	isPopoverOpen: boolean;
	onContextMenu: (event: React.MouseEvent) => void;
	onHeaderClick: () => void;
	onPopoverClose: () => void;
}

interface BadgeConfig {
	colorScheme: string;
	shortLabel: string;
}

const BADGE_CONFIG: Record<QuoteColumnType, BadgeConfig> = {
	description: { colorScheme: "gray", shortLabel: "Desc" },
	unit_of_measurement: { colorScheme: "blue", shortLabel: "UoM" },
	response_quantity: { colorScheme: "green", shortLabel: "Qty" },
	response_value: { colorScheme: "purple", shortLabel: "Value" },
	saving: { colorScheme: "red", shortLabel: "Saving" },
	response_number: { colorScheme: "orange", shortLabel: "Num" },
	response_text: { colorScheme: "teal", shortLabel: "Text" },
	total: { colorScheme: "yellow", shortLabel: "Total" },
};

/**
 * Single sortable column header rendered in the SpreadsheetGrid header row.
 * Click → open edit popover (parent decides). Right-click → context menu.
 * Drag handle uses @dnd-kit/sortable — drag listeners are attached only to the
 * grip icon so clicks on the rest of the cell still open the popover.
 *
 * The DnD glue is intentionally a thin wrapper around `useSortable`. If the
 * library changes, the only things that need to move are the import, the
 * `useSortable` call, and the style object — the rest of the component is
 * presentational.
 */
export const ColumnHeaderCell = ({
	column,
	columnIndex: _columnIndex,
	columnCount: _columnCount,
	isPopoverOpen: _isPopoverOpen,
	onHeaderClick,
	onContextMenu,
	onPopoverClose,
}: ColumnHeaderCellProps) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
		useSortable({ id: column.localId });

	const badge = BADGE_CONFIG[column.columnType];

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === "Enter") {
			event.preventDefault();
			onHeaderClick();
		}
		if (event.key === "Escape") {
			event.stopPropagation();
			onPopoverClose();
		}
	};

	return (
		<Box
			ref={setNodeRef}
			role="columnheader"
			aria-label={column.label}
			tabIndex={0}
			data-testid={`column-header-${column.localId}`}
			onClick={onHeaderClick}
			onContextMenu={onContextMenu}
			onKeyDown={handleKeyDown}
			minW="110px"
			py={1.5}
			bg="gray.50"
			border="1px solid"
			borderColor="gray.200"
			cursor="pointer"
			_hover={{ bg: "gray.100" }}
			_focus={{ outline: "2px solid", outlineColor: "blue.400" }}
			px={2}
			style={style}
			{...attributes}
		>
			<Flex direction="column" align="stretch" gap={0.5}>
				<Flex align="center" gap={1} minW={0}>
					<Box
						as="span"
						aria-label="Drag to reorder column"
						data-testid={`column-drag-handle-${column.localId}`}
						cursor="grab"
						color="gray.400"
						flexShrink={0}
						{...listeners}
						onClick={(e: React.MouseEvent) => e.stopPropagation()}
					>
						<FiMenu size={14} />
					</Box>
					<Box
						flex="1"
						fontSize="sm"
						fontWeight="medium"
						minW={0}
						whiteSpace="normal"
						wordBreak="break-word"
					>
						{column.label}
					</Box>
				</Flex>
				<Box>
					<Badge colorScheme={badge.colorScheme} fontSize="2xs">
						{badge.shortLabel}
					</Badge>
				</Box>
			</Flex>
		</Box>
	);
};

export default ColumnHeaderCell;
